// pages/Confirm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Text,
  Flex,
  Circle,
  Icon,
  Progress,
  Button,
  Heading,
  HStack,
  Badge,
  Divider
} from '@chakra-ui/react';
import { 
  MdCheck, 
  MdArrowBack, 
  MdLocalShipping, 
  MdAttachMoney,
  MdAccessTime,
  MdCategory,
  MdMessage,
  MdHome,
  MdSearch,
  MdDescription,
  MdNotifications,
  MdLogout
} from 'react-icons/md';
import { getNegotiationStatus } from '../api/auth';


// Animation component for the negotiation in progress
const NegotiationProgressAnimation = () => {
  const [progressValue, setProgressValue] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgressValue(prev => {
        if (prev >= 100) return 0;
        return prev + 4;
      });
    }, 300);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <Box width="100%" px={10} py={6}>
      <Progress 
        value={progressValue} 
        size="lg" 
        colorScheme="orange" 
        borderRadius="full"
        height="8px"
        bg="gray.100"
        isAnimated
        hasStripe
      />
    </Box>
  );
};

// Bottom Navigation component
const BottomNav = () => {
  const navigate = useNavigate();
  
  const navItems = [
    { icon: MdSearch, label: 'Search', path: '/search' },
    { icon: MdDescription, label: 'Inspection', path: '/inspection' },
    { icon: MdHome, label: 'Home', path: '/home' },
    { icon: MdNotifications, label: 'Notifications', path: '/notifications' },
  ];

  return (
    <Flex 
      position="fixed" 
      bottom={0} 
      left={0} 
      right={0} 
      bg="white" 
      boxShadow="0 -2px 10px rgba(0,0,0,0.05)"
      justify="space-between" 
      py={2}
      px={4}
      zIndex={10}
    >
      {navItems.map((item) => (
        <VStack 
          key={item.path} 
          spacing={0}
          opacity={item.path === '/home' ? 1 : 0.7}
          onClick={() => navigate(item.path)}
          cursor="pointer"
          align="center"
          flex={1}
        >
          {item.path === '/home' ? (
            <Flex
              align="center"
              justify="center"
              bg="#FD984F"
              width="50px"
              height="50px"
              borderRadius="full"
              mb={1}
              mt="-20px"
              boxShadow="md"
            >
              <Icon as={item.icon} color="white" boxSize={6} />
            </Flex>
          ) : (
            <Icon as={item.icon} boxSize={5} />
          )}
          <Text fontSize="xs">{item.label}</Text>
        </VStack>
      ))}
    </Flex>
  );
};

export default function Confirm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Read rates from localStorage
  const lowerRate = localStorage.getItem('lowerRate');
  const upperRate = localStorage.getItem('upperRate');

      // Start conversation and trigger widget
      useEffect(() => {
        const initiateCall = async () => {
      
          // add a delay before we check for
          await new Promise((res) => setTimeout(res, 1000));
    
          // Step 1: Wait for the widget to load
          const widget = document.querySelector('elevenlabs-convai');
          if (!widget) {
            console.error('ElevenLabs widget not found.');
            return;
          }
          const storedDriverId = localStorage.getItem('driverId');
          const storedSessionId = localStorage.getItem('sessionId');
          widget.setAttribute('dynamic-variables', JSON.stringify({ driver_id: storedDriverId, session_id: storedSessionId, context:"Don't follow any of the above steps, user is on the final load booking negotiation confirmation page, use the tool transfer_to_agent, if you don't have the tool transfer_to_agent then check for negotiation updates using the tool negotiation_status and negotiation_raw_update, if the negotiation is in progress and relay updates." }));
        };
      
        initiateCall();
      }, []);

  useEffect(() => {
    const driverId = localStorage.getItem('driverId');
    const POLL_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    // If no driverId, don't start polling
    if (!driverId) {
      setLoading(false);
      return;
    }
    
    let intervalId = null;
    let timeoutId = null;
    
    const checkStatus = async () => {
      try {
        // Check if driverId still exists in localStorage
        if (!localStorage.getItem('driverId')) {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          setLoading(false);
          return false;
        }

        const response = await getNegotiationStatus(driverId);
        let negotiation_details = response.data;
        
        if (!response || !response.data.negotiation_status) {
          console.error('Invalid response format:', response);
          return false;
        }

        // Set initial in-progress state if not set
        if (!status) {
          setStatus({
            ...negotiation_details,
            negotiation_status: 'in_progress'
          });
        }
        
        // Only update status if it's completed
        if (negotiation_details.negotiation_status === 'completed') {
          setStatus(negotiation_details);
          setLoading(false);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error in checkStatus:', error);
        setLoading(false);
        return false;
      }
    };

    // Initial check and only set up polling if not completed
    const initialCheck = async () => {
      try {
        const isCompleted = await checkStatus();
        
        if (!isCompleted) {
          intervalId = setInterval(async () => {
            const isCompleted = await checkStatus();
            if (isCompleted) {
              clearInterval(intervalId);
              clearTimeout(timeoutId);
            }
          }, 5000);

          // Set timeout to stop polling after 10 minutes
          timeoutId = setTimeout(() => {
            clearInterval(intervalId);
            setLoading(false);
            setStatus(prev => ({
              ...prev,
              negotiation_status: 'timeout',
              broker_notes: 'Negotiation timed out after 10 minutes. Please try again.'
            }));
          }, POLL_TIMEOUT);
        }
      } catch (error) {
        console.error('Error in initialCheck:', error);
        setLoading(false);
      }
    };

    initialCheck();

    // Cleanup function
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-b, #FFF3E3, #E8AA7A)"
      pb="70px" // Space for bottom navigation
    >
      {/* Header with back button and logout */}
      <Flex px={4} py={4} justify="space-between" align="center">
        <Flex align="center">
          <Icon 
            as={MdArrowBack} 
            boxSize={6} 
            mr={2} 
            cursor="pointer" 
            onClick={() => navigate('/search')} 
          />
          <Text fontSize="xl" fontWeight="bold">Confirmation</Text>
        </Flex>
        <Icon
          as={MdLogout}
          boxSize={6}
          cursor="pointer"
          onClick={() => {
            localStorage.clear();
            navigate('/login');
          }}
        />
      </Flex>

      {/* Main content */}
      <Box px={4} pb={16}>
        <Box 
          bg="white" 
          borderRadius="2xl" 
          overflow="hidden" 
          boxShadow="md"
          mb={4}
        >
          {/* Top section */}
          <Box 
            bg="#FFF3E3" 
            py={6} 
            px={4} 
            textAlign="center"
          >
            <Text 
              fontSize="2xl" 
              fontWeight="bold" 
              color="#202020"
            >
              Booking at <Text as="span" color="#FD984F">${lowerRate}-${upperRate}/mi</Text>
            </Text>
          </Box>

          {/* Status section */}
          <Box p={6}>
            {loading && status?.negotiation_status === 'in_progress' ? (
              <VStack spacing={6}>
                <Box width="full">
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight="semibold" color="gray.600">Negotiation Status</Text>
                    <Badge colorScheme="orange" borderRadius="full" px={3} py={1}>
                      In Progress
                    </Badge>
                  </HStack>
                  
                  <NegotiationProgressAnimation />
                  
                  <Text 
                    textAlign="center" 
                    color="gray.600" 
                    fontSize="sm" 
                    mt={4}
                  >
                    We're negotiating with the broker to get you the best deal. This usually takes 2-3 minutes.
                  </Text>
                </Box>
                
                <Divider />
                
                <VStack align="stretch" width="full" spacing={4}>
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={MdAttachMoney} color="gray.500" />
                      <Text color="gray.600">Target Rate</Text>
                    </HStack>
                    <Text fontWeight="bold">${lowerRate}-${upperRate}/mi</Text>
                  </HStack>
                  
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={MdAccessTime} color="gray.500" />
                      <Text color="gray.600">Started</Text>
                    </HStack>
                    <Text>{new Date().toLocaleTimeString()}</Text>
                  </HStack>
                </VStack>
              </VStack>
            ) : !loading && status?.negotiation_status === 'completed' ? (
              <VStack spacing={6} align="stretch">
                <Box width="full">
                  <HStack justify="space-between" mb={6}>
                    <Text fontWeight="semibold" color="gray.600">Negotiation Status</Text>
                    <Badge 
                      colorScheme={status.negotiation_success ? "green" : "red"} 
                      borderRadius="full" 
                      px={3} 
                      py={1}
                    >
                      {status.negotiation_success ? "Success" : "Failed"}
                    </Badge>
                  </HStack>
                  
                  <Flex 
                    justify="center" 
                    align="center" 
                    direction="column"
                    mb={6}
                  >
                    <Circle 
                      size="70px" 
                      bg={status.negotiation_success ? "green.100" : "red.100"} 
                      mb={3}
                    >
                      <Icon 
                        as={status.negotiation_success ? MdCheck : MdAttachMoney} 
                        color={status.negotiation_success ? "green.500" : "red.500"} 
                        boxSize={8} 
                      />
                    </Circle>
                    <Text 
                      fontSize="xl" 
                      fontWeight="bold" 
                      color={status.negotiation_success ? "green.500" : "red.500"}
                    >
                      {status.negotiation_success ? 
                        `Rate Secured: $${status.negotiated_rate_per_mile}/mi` : 
                        "Negotiation Failed"
                      }
                    </Text>
                  </Flex>
                </Box>
                
                <Divider />
                
                <VStack align="stretch" width="full" spacing={4}>
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={MdCategory} color="gray.500" />
                      <Text color="gray.600">Commodity</Text>
                    </HStack>
                    <Text fontWeight="medium">{status.commodity || "Not specified"}</Text>
                  </HStack>
                  
                  <HStack align="flex-start" justify="space-between">
                    <HStack align="flex-start">
                      <Icon as={MdMessage} color="gray.500" mt={1} />
                      <Text color="gray.600">Broker Notes</Text>
                    </HStack>
                    <Text maxW="60%" textAlign="right">{status.broker_notes || "No notes provided"}</Text>
                  </HStack>
                  
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={MdLocalShipping} color="gray.500" />
                      <Text color="gray.600">Original Rate</Text>
                    </HStack>
                    <Text>${lowerRate}-${upperRate}/mi</Text>
                  </HStack>
                </VStack>
              </VStack>
            ) : !loading && status?.negotiation_status === 'timeout' ? (
              <VStack spacing={4}>
                <Icon as={MdAccessTime} boxSize={10} color="red.500" />
                <Text color="red.500" fontWeight="bold">Negotiation Timed Out</Text>
                <Text color="gray.600" textAlign="center">{status.broker_notes}</Text>
                <Button 
                  leftIcon={<MdSearch />} 
                  onClick={() => navigate('/search')}
                  colorScheme="orange"
                  variant="outline"
                >
                  Back to Search
                </Button>
              </VStack>
            ) : (
              <VStack spacing={4}>
                <Text color="gray.500">Unable to load negotiation status</Text>
                <Button 
                  leftIcon={<MdSearch />} 
                  onClick={() => navigate('/search')}
                  colorScheme="orange"
                  variant="outline"
                >
                  Back to Search
                </Button>
              </VStack>
            )}
          </Box>
        </Box>
        
        {/* Action button */}
        <Button
          width="full"
          size="lg"
          bg="#FD984F"
          color="white"
          _hover={{ bg: "#E5873C" }}
          _active={{ bg: "#D97A38" }}
          borderRadius="full"
          boxShadow="md"
          height="60px"
          fontSize="lg"
          onClick={() => navigate('/search')}
          mt={4}
        >
          {loading ? "View Other Loads" : status?.negotiation_success ? "Continue to Booking" : "Back to Search"}
        </Button>
      </Box>
      
      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
}
